import { useState } from 'react';
import { C } from '@/theme';
import { Truck, Store, Check, PackagePlus, Gift, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';
import { datumDe, heuteIso } from '@/lib/format';
import { ChecklistGitter } from './ChecklistGitter';
import type { Bestellung, DetailSendung } from '@/types/detail';

type Props = {
  /** data-tour-Name fuer die Einfuehrungstour. */
  anker?: string;
  bestellung: Bestellung;
  /** Endzustands-Rueckfrage aus der Route (B15). */
  bestaetigeAenderung: () => Promise<boolean>;
  /**
   * Oeffnet den Sendungs-Dialog, OHNE den Zustand anzufassen (#50).
   *
   * Bis A7.4 fuehrte der einzige Weg zu einer Sendung ueber „Alle
   * eingetroffen — bereit setzen". Fuer eine Teilsendung hiess das: erst
   * die Bestellung auf „Bereit" setzen, obwohl sie nicht bereit ist. Der
   * Weg zur Teilsendung fuehrte ueber einen Zustand, der die Unwahrheit
   * sagt — und im Brett stand sie danach als abholbereit.
   */
  onSendungErstellen: () => void;
};

export function SendungenSektion({
  anker,
  bestellung,
  bestaetigeAenderung,
  onSendungErstellen
}: Props) {
  // Anzubieten ist der Knopf nur, wenn ueberhaupt noch etwas in eine
  // Sendung koennte: eine Position, die vollstaendig eingetroffen und
  // noch nicht verplant ist (B23/B24). Sonst ist er ein Versprechen, das
  // der Dialog dahinter nicht halten kann.
  const verplant = new Map<number, number>();
  for (const s of bestellung.sendungen) {
    for (const sp of s.positionen) {
      verplant.set(sp.position_id, (verplant.get(sp.position_id) ?? 0) + sp.menge);
    }
  }
  const etwasZuVersenden = bestellung.positionen.some(
    (p) =>
      p.menge_eingegangen >= p.menge_wirksam && p.menge_wirksam - (verplant.get(p.id) ?? 0) > 0
  );
  // In einem Endzustand ist der Vorgang erledigt; eine neue Sendung waere
  // ein Rueckschritt (dieselbe Ueberlegung wie beim Bereit-Knopf).
  const zeigeKnopf = etwasZuVersenden && !bestellung.zustand.ist_endzustand;
  // E55: Bei Glomer kommt jede Sendung aus Shopify — easyDPD oder der
  // Knopf „Ausführen" dort, auch teilweise. Hier wird nichts angelegt.
  const ausShop = bestellung.shopify_order_id !== null;

  return (
    <section data-tour={anker}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-serif text-base text-ink">Sendungen</h3>
        {zeigeKnopf && !ausShop && (
          <Button
            data-nutzung="sendung_erstellen"
            variant="secondary"
            data-tour="detail-sendung-erstellen"
            onClick={onSendungErstellen}
          >
            <PackagePlus className="h-4 w-4" />
            Sendung erstellen
          </Button>
        )}
      </div>
      {/* Der leere Zustand darf dem Zustand der Bestellung nicht
          widersprechen (S2-006e). Seit A11 fuehrt bei Versand der Knopf
          „Alle eingetroffen — abschliessen“ direkt in den
          Endzustand, OHNE eine Sendung anzulegen. Oben stand dann
          „Abgeschlossen“ und hier „Noch keine Sendung erstellt“ — das liest
          sich wie ein Versaeumnis, ist aber der vorgesehene Weg.

          Der Satz nennt bewusst nicht den Versandweg: ist_endzustand
          tragen auch „Storniert“ und „Remittiert“, und dort waere
          „direkt als versandt abgeschlossen“ schlicht falsch. */}
      {ausShop && !bestellung.zustand.ist_endzustand && (
        <p className="mb-2 text-xs text-ink-faint" data-testid="sendungen-aus-shopify">
          Ausgeführt wird in Shopify — mit easyDPD oder „Ausführen“, auch teilweise. Jede
          Ausführung erscheint hier als Sendung.
        </p>
      )}
      {bestellung.sendungen.length === 0 ? (
        <p className="rounded-md border border-dashed border-line bg-cream-warm px-3 py-4 text-center text-sm text-ink-faint">
          {bestellung.zustand.ist_endzustand
            ? 'Zu dieser Bestellung wurde keine Sendung angelegt — der Vorgang wurde ohne Sendung beendet.'
            : 'Noch keine Sendung erstellt.'}
        </p>
      ) : (
        <div className="space-y-2">
          {bestellung.sendungen.map((s) => (
            <SendungCard
              key={s.id}
              sendung={s}
              bestellnr={bestellung.bestellnr}
              bestaetigeAenderung={bestaetigeAenderung}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Das Kreuzband-Motiv für Geschenk-Sendungen (A9.2).
 *
 * Zwei gekreuzte Bänder, gekachelt — die Andeutung eines verschnürten
 * Päckchens. Inline-SVG statt einer Bilddatei: es ist ein paar Zeilen
 * groß, braucht keinen zweiten Abruf und nimmt die Goldfarbe aus dem
 * Stylesheet an.
 *
 * `aria-hidden` und `pointer-events-none`: Das Muster ist Atmosphäre.
 * Die Auskunft trägt das Geschenk-Symbol in der Zeile darüber — wer
 * vorliest, soll nicht zweimal dasselbe hören.
 */
function GeschenkMuster() {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity: 0.05, color: C.goldDark }}
    >
      <defs>
        <pattern id="geschenk-kreuzband" width="28" height="28" patternUnits="userSpaceOnUse">
          <path
            d="M0 14 L28 14 M14 0 L14 28"
            stroke="currentColor"
            strokeWidth="6"
            fill="none"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#geschenk-kreuzband)" />
    </svg>
  );
}

function SendungCard({
  sendung,
  bestellnr,
  bestaetigeAenderung
}: {
  sendung: DetailSendung;
  bestellnr: string;
  bestaetigeAenderung: () => Promise<boolean>;
}) {
  const [openVersand, setOpenVersand] = useState(false);
  // Ortszeit, nicht UTC — sonst steht abends das Datum von gestern drin.
  const [datum, setDatum] = useState(heuteIso());
  const [trackingNr, setTrackingNr] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  // Abholung ist kein Versand: keine Tracking-Nr, eigener Endpoint.
  const istAbholung = sendung.versandart?.code === 'abholung';
  // Seit Migration 012 wird die Abholung persistiert (B25).
  const erledigt = istAbholung ? sendung.abgeholt_am !== null : sendung.versand_datum !== null;

  /**
   * Das Formular öffnen — mit dem, was WIRKLICH gespeichert ist (A49).
   *
   * Bis hierher stand im Datumsfeld immer „heute" und im Nummernfeld
   * nichts, weil beide Zustände einmal beim Einhängen gesetzt wurden.
   * Solange es das Formular nur für die erste Buchung gab, war das
   * richtig. Seit es auch korrigiert, ist es falsch: Wer die
   * Sendungsnummer nachträgt, hätte damit unbemerkt das Versanddatum
   * auf heute gezogen — dieselbe Sorte Fehler wie ein Häkchen, das von
   * der vorigen Sendung stehenbleibt.
   *
   * Deshalb beim Öffnen frisch aus der Sendung (dasselbe Muster wie in
   * KundeEditor, B29): Wer korrigiert, sieht erst, was dort steht.
   */
  function formularOeffnen(): void {
    setDatum((istAbholung ? sendung.abgeholt_am : sendung.versand_datum) ?? heuteIso());
    setTrackingNr(sendung.tracking_nr ?? '');
    setOpenVersand(true);
  }

  async function markiere(): Promise<void> {
    if (laeuft) return; // Doppelklick-Schutz (B25)
    // DIE EINE AUSNAHME VON B15 (A50).
    //
    // Die Endzustands-Rueckfrage ist eine weiche Sperre gegen
    // VERSEHENTLICHE Aenderungen an erledigten Vorgaengen. Sie gilt
    // hier weiter fuer die erste Buchung — „Versendet markieren" an
    // einer Bestellung, die schon aus einem anderen Grund im
    // Endzustand steht, ist genau so ein Fall.
    //
    // Sie gilt NICHT mehr fuer die Korrektur einer bereits gebuchten
    // Sendung. Wer die hier bearbeitet, hat den Endzustand nicht
    // versehentlich getroffen: Der Endzustand ist die FOLGE dieser
    // Sendung (seit A46 schliesst das Buchen die Bestellung ab). Die
    // Rueckfrage warnte dort vor etwas, das derselbe Mensch soeben
    // selbst herbeigefuehrt hat — und traf damit den Normalweg, fuer
    // den A49 gebaut wurde: Das Versandetikett wird erst nach dem
    // Anlegen gedruckt, die Sendungsnummer also immer nachgetragen.
    //
    // Ueberall sonst bleibt B15, wie sie ist. Das ist eine benannte
    // Ausnahme, keine Aufweichung der Regel.
    if (!erledigt && !(await bestaetigeAenderung())) return;
    setLaeuft(true);
    const pfad = istAbholung ? '/sendungen/abgeholt.php' : '/sendungen/versendet.php';
    const body = istAbholung
      ? { id: sendung.id, abgeholt_am: datum }
      : {
          id: sendung.id,
          versand_datum: datum,
          tracking_nr: trackingNr.trim() === '' ? null : trackingNr.trim()
        };
    try {
      await apiFetch(pfad, { method: 'POST', body: JSON.stringify(body) });
      qc.invalidateQueries({ queryKey: ['bestellung', bestellnr] });
      setOpenVersand(false);
      // Was der Melder sagt, muss zu dem passen, was gerade geschah:
      // „als versendet markiert" nach einer Korrektur wäre eine
      // Auskunft über einen Schritt, den niemand gemacht hat (A49).
      toast(
        'success',
        erledigt
          ? 'Angaben gespeichert.'
          : istAbholung
            ? 'Sendung als abgeholt markiert.'
            : 'Sendung als versendet markiert.'
      );
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unbekannt';
      toast('error', `Sendung konnte nicht gespeichert werden: ${code}`);
    } finally {
      setLaeuft(false);
    }
  }

  const status = istAbholung
    ? sendung.abgeholt_am !== null
      ? `Abgeholt am ${datumDe(sendung.abgeholt_am)}`
      : 'Zur Abholung bereit'
    : sendung.versand_datum !== null
      ? `Versandt am ${datumDe(sendung.versand_datum)}`
      : 'Vorbereitet';

  return (
    <div className="geschenk-karte relative overflow-hidden rounded-md border border-line bg-cream p-3">
      {/* A9.2: Ist die Sendung als Geschenk gekennzeichnet, bekommt die
          Karte ein zartes Kreuzband-Motiv — zwei gekreuzte Bänder, wie
          ein verschnürtes Päckchen. Opazität 0,05 in der Gold-Palette:
          es soll auffallen, ohne die Zeile darüber zu stören.

          Das Muster allein genügt aber nicht. Ein Hintergrund ist
          atmosphärisch und nicht benennbar — deshalb steht neben der
          Versandart zusätzlich ein Geschenk-Symbol mit Beschriftung.
          Wer die Karte sieht, spürt es; wer die Zeile liest, weiß es.

          Im Druck verschwindet das Muster (siehe .geschenk-karte in
          web/src/index.css) — auf dem Bestellzettel gilt das
          Minimal-Zettel-Prinzip, und ein Raster über einem
          Schwarzweißdruck wäre ohnehin nur Schmutz. */}
      {sendung.ist_geschenk && <GeschenkMuster />}
      <div className="relative flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink">
        {istAbholung ? (
          <Store className="h-4 w-4 text-ink-soft" />
        ) : (
          <Truck className="h-4 w-4 text-ink-soft" />
        )}
        <span className="font-medium">{sendung.versandart?.label ?? 'Sendung'}</span>
        {sendung.ist_geschenk && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-gold-dark/50 px-2 py-0.5 text-xs text-gold-dark"
            title="Diese Sendung wird als Geschenk verpackt"
          >
            <Gift aria-hidden className="h-3.5 w-3.5" />
            Geschenk
          </span>
        )}
        {sendung.aus_shopify && (
          <span
            className="rounded-full border border-line px-2 py-0.5 text-xs text-ink-soft"
            title="Diese Sendung ist eine Ausführung in Shopify. Ändern geht nur dort."
          >
            aus Shopify
          </span>
        )}
        <span className="text-ink-faint">·</span>
        <span className="text-ink-faint">{status}</span>
        {sendung.tracking_nr && (
          <span className="text-xs text-ink-faint">· #{sendung.tracking_nr}</span>
        )}
        {/* A49: DER WEG ZURÜCK AN DIE ANGABEN.
            ----------------------------------------------------------
            Seit A46 trägt eine Versand-Sendung ihr Datum schon beim
            Anlegen. Damit galt sie sofort als erledigt, der Knopf fiel
            weg — und mit ihm das Formular. Das Versandetikett wird
            aber in der Regel erst DANACH gedruckt: Die Sendungsnummer
            ist beim Anlegen noch gar nicht bekannt, und es gab keinen
            Weg mehr, sie einzutragen. Vor A46 ging das wenigstens
            einmal.

            Der Knopf bleibt deshalb stehen und wechselt nur seinen
            Wortlaut. „Versendet markieren" wäre falsch, wenn die
            Sendung schon versendet IST — und „Angaben ändern" allein
            ließe offen, welche. Also sagt er, was dahinter liegt:
            bei Versand Datum und Nummer, bei Abholung nur das Datum.

            BEIDE WEGE, nicht nur Versand: Ein Abholdatum lässt sich
            genauso vertippen, und eine Karte, an der es einmal geht
            und einmal nicht, erzeugt nächste Woche eine Rückfrage. */}
        {/* E55: Eine Shopify-Ausführung ändert sich nur in Shopify —
            der Server weist es ohnehin ab (shopSperreSendung). */}
        {!sendung.aus_shopify && (
        <Button
          variant="secondary"
          className="ml-auto"
          onClick={() => (openVersand ? setOpenVersand(false) : formularOeffnen())}
        >
          {erledigt ? <Pencil className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          {erledigt
            ? istAbholung
              ? 'Abholdatum ändern'
              : 'Versandangaben ändern'
            : istAbholung
              ? 'Abgeholt markieren'
              : 'Versendet markieren'}
        </Button>
        )}
      </div>
      {sendung.positionen.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-ink-soft">
          {sendung.positionen.map((sp) => (
            <li key={sp.position_id}>
              {sp.titel}
              {sp.menge > 1 ? ` (×${sp.menge})` : ''}
            </li>
          ))}
        </ul>
      )}

      {/* A58 (#143): Die Haken AN DIESER Sendung.
          ----------------------------------------------------------
          'verpackt' und 'versandt' tragen seit dem ersten Schema
          scope='sendung' und wurden nirgends angezeigt — zwei von acht
          ausgelieferten Punkten waren tote Zeilen. Hier stehen sie,
          je Sendung getrennt: Was an der einen verpackt ist, sagt
          nichts über die andere.

          UNTER den Positionen und nicht darüber: Erst was drin ist,
          dann was damit geschehen ist. */}
      <SendungChecklist sendung={sendung} bestellnr={bestellnr} />

      {openVersand && (
        <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-line pt-3">
          <label className="flex flex-col gap-1 text-xs text-ink-soft">
            {istAbholung ? 'Abholdatum' : 'Versanddatum'}
            <input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              className="min-h-[44px] rounded border border-line bg-cream px-2 py-1 text-sm"
            />
          </label>
          {!istAbholung && (
            <label className="flex flex-col gap-1 text-xs text-ink-soft">
              Sendungsnummer (optional)
              {/* KEIN inputMode="numeric" (CLAUDE.md, Falle 18): UPS
                  beginnt mit „1Z". Ein reines Zifferfeld machte diese
                  Nummern auf iOS uneingebbar — und genau sie ist der
                  Wert, für den es diesen Weg gibt. */}
              <input
                type="text"
                value={trackingNr}
                maxLength={100}
                onChange={(e) => setTrackingNr(e.target.value)}
                placeholder="DPD…"
                className="min-h-[44px] rounded border border-line bg-cream px-2 py-1 text-sm"
              />
            </label>
          )}
          <Button variant="primary" onClick={() => void markiere()} disabled={laeuft}>
            {laeuft ? 'Speichern …' : 'Speichern'}
          </Button>
          <Button variant="ghost" onClick={() => setOpenVersand(false)} disabled={laeuft}>
            Abbrechen
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Die Checkliste einer Sendung (A58, #143).
 *
 * Eigene Komponente und nicht ein paar Zeilen in SendungCard: Sie hält
 * einen eigenen Ladezustand, und ein zweiter `useState` in einer Karte,
 * die schon einen für das Versandformular hat, wäre der Anfang von
 * Verwicklungen.
 *
 * Das Gitter kommt aus ChecklistGitter — dasselbe Bauteil wie an der
 * Bestellung. Was hier fehlt, fehlt mit Absicht: keine Überschrift
 * „Bearbeitungsstand" (die Karte hat schon eine Kopfzeile) und kein
 * Benachrichtigungs-Zusatz (der gehört zur Bestellung).
 */
function SendungChecklist({
  sendung,
  bestellnr
}: {
  sendung: DetailSendung;
  bestellnr: string;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  // `?? []` und nicht `sendung.checkliste.length`: Antwortet der Server
  // ohne das Feld — eine ältere Fassung, ein Zwischenspeicher aus der
  // Zeit vor dem Einspielen, ein Prüfstand mit anderer Attrappe —,
  // risse `.length` die ganze Detailseite mit. Ein Abschnitt, der leer
  // bleibt, ist ein Ärgernis; eine Detailseite, die deshalb weiß
  // bleibt, ist ein Ausfall (dieselbe Lehre wie in ListenAbschnitt,
  // A41 — dort kam sie aus A31).
  const eintraege = sendung.checkliste ?? [];
  if (eintraege.length === 0) return null;

  async function umschalten(code: string, wert: boolean): Promise<void> {
    if (laeuft) return; // Doppeltipp-Schutz, wie beim Versandformular
    setLaeuft(true);
    try {
      await apiFetch('/sendungen/checklist.php', {
        method: 'POST',
        body: JSON.stringify({ id: sendung.id, feld: code, wert })
      });
      qc.invalidateQueries({ queryKey: ['bestellung', bestellnr] });
    } catch (err) {
      const fehler = err instanceof ApiError ? err.code : 'unbekannt';
      toast('error', `Haken konnte nicht gespeichert werden: ${fehler}`);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="relative mt-3 border-t border-line pt-3">
      <ChecklistGitter
        eintraege={eintraege}
        // Einzahl, weil an der Sendung heute genau ein berechneter Punkt
        // steht. Steht dort einmal ein zweiter, fällt dieser Satz auf —
        // besser als ein „diese ergeben sich", das bei einem einzigen
        // Punkt falsch klingt und niemandem auffällt.
        hinweis={'„Versandt“ ergibt sich aus dem Versanddatum dieser Sendung.'}
        onToggle={(code, wert) => void umschalten(code, wert)}
      />
    </div>
  );
}
